import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Ban, Plus } from 'lucide-react';
import { DocLang, PartnerType, todayIso, versionTag } from '@clawhire/shared';
import { api } from '@/lib/api';
import { EMPTY, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';
import { SectionCard } from '../components/SectionCard';
import { useInvalidatePositions, usePartners } from '../queries';
import type { JdBriefing, PositionDetail } from '../types';

export interface BriefingsSectionProps {
  position: PositionDetail;
  canEdit: boolean;
}

/** Which supplier got which JD version, stop-sourcing state, and "Record briefing" (§6.1). */
export function BriefingsSection({ position, canEdit }: BriefingsSectionProps) {
  const invalidate = useInvalidatePositions();
  const [formOpen, setFormOpen] = React.useState(false);

  const stop = useMutation({
    mutationFn: (briefing: JdBriefing) =>
      api.post(
        `/positions/${encodeURIComponent(position.id)}/briefings/${encodeURIComponent(briefing.id)}/stop-sourcing`,
        {},
      ),
    onSuccess: async (_res, briefing) => {
      toast.success(`Stop sourcing recorded for ${briefing.partner.name}`);
      await invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <SectionCard
      title="Briefings"
      description="Suppliers and freelancers who received the JD. The first briefing moves a New position to Sourcing."
      actions={
        canEdit ? (
          <Button
            size="sm"
            variant={formOpen ? 'secondary' : 'default'}
            onClick={() => setFormOpen((o) => !o)}
          >
            <Plus aria-hidden="true" /> Record briefing
          </Button>
        ) : null
      }
    >
      {formOpen && canEdit ? (
        <RecordBriefingForm
          position={position}
          onDone={() => setFormOpen(false)}
          onCancel={() => setFormOpen(false)}
        />
      ) : null}

      {position.briefings.length === 0 ? (
        <p className="text-sm text-muted-foreground">No briefings yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <caption className="sr-only">Briefings</caption>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Supplier</TableHead>
                <TableHead>JD version</TableHead>
                <TableHead>Sent on</TableHead>
                <TableHead>Stop sourcing</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {position.briefings.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{b.partner.name}</span>
                      {b.partner.type ? (
                        <EnumBadge def={PartnerType} value={b.partner.type} size="sm" />
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell>
                    {b.jdVersion ? (
                      <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                        {b.jdVersion.reference} {versionTag(b.jdVersion.version)}
                        <EnumBadge def={DocLang} value={b.jdVersion.lang} size="sm" />
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{EMPTY}</span>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    <time dateTime={b.sentOn}>{formatDate(b.sentOn)}</time>
                  </TableCell>
                  <TableCell>
                    {b.stopSourcingSentOn ? (
                      <time dateTime={b.stopSourcingSentOn} className="tabular-nums">
                        {formatDate(b.stopSourcingSentOn)}
                      </time>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-muted-foreground">{EMPTY}</span>
                        {canEdit ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            loading={stop.isPending && stop.variables?.id === b.id}
                            onClick={() => stop.mutate(b)}
                          >
                            <Ban aria-hidden="true" /> Send stop sourcing
                          </Button>
                        ) : null}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[16rem] truncate" title={b.notes ?? undefined}>
                    {b.notes || <span className="text-muted-foreground">{EMPTY}</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </SectionCard>
  );
}

function RecordBriefingForm({
  position,
  onDone,
  onCancel,
}: {
  position: PositionDetail;
  onDone: () => void;
  onCancel: () => void;
}) {
  const invalidate = useInvalidatePositions();
  const suppliers = usePartners('SUPPLIER');
  const freelancers = usePartners('FREELANCER');
  const partners = React.useMemo(
    () => [
      ...(suppliers.data ?? []).map((p) => ({ value: p.id, label: `${p.name} (Supplier)` })),
      ...(freelancers.data ?? []).map((p) => ({ value: p.id, label: `${p.name} (Freelancer)` })),
    ],
    [suppliers.data, freelancers.data],
  );
  const [partner, setPartner] = React.useState<string | null>(null);
  const [jdVersion, setJdVersion] = React.useState<string | null>(position.currentJdVersionId);
  const [sentOn, setSentOn] = React.useState<string | null>(todayIso());
  const [notes, setNotes] = React.useState('');

  const add = useMutation({
    mutationFn: () =>
      api.post(`/positions/${encodeURIComponent(position.id)}/briefings`, {
        partner,
        jdVersion: jdVersion ?? undefined,
        sentOn: sentOn ?? undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: async () => {
      toast.success('Briefing recorded');
      await invalidate();
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <form
      className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-2 lg:grid-cols-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (partner) add.mutate();
      }}
      aria-label="Record briefing"
    >
      <div className="space-y-1">
        <Label htmlFor="briefing-partner">Supplier / freelancer</Label>
        <SimpleSelect
          id="briefing-partner"
          value={partner}
          options={partners}
          onChange={setPartner}
          placeholder={suppliers.isLoading || freelancers.isLoading ? 'Loading…' : 'Select…'}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="briefing-version">JD version</Label>
        <SimpleSelect
          id="briefing-version"
          value={jdVersion}
          allowEmpty
          emptyLabel="No version"
          options={position.jdVersions.map((v) => ({
            value: v.id,
            label: `${v.reference} ${versionTag(v.version)} (${v.lang})`,
          }))}
          onChange={setJdVersion}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="briefing-date">Sent on</Label>
        <DateInput id="briefing-date" value={sentOn} onChange={setSentOn} className="w-full" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="briefing-notes">Notes</Label>
        <Input id="briefing-notes" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
      </div>
      <div className="flex justify-end gap-2 sm:col-span-2 lg:col-span-4">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!partner} loading={add.isPending}>
          Record
        </Button>
      </div>
    </form>
  );
}
